import { CardLayout, MTable } from "../v3";
import { accountReportColumns } from "../../pages/report/constants";
import { AnalyticsService } from "../../services";
import { useReportPdfDownload } from "../../hooks/useReportPdfDownload";
import { sumReportField, withReportRowMeta, toAccountDetailedTableRow } from "../../utils/report/reportRows.util";
import type {
  AccountReportDetailedRow,
  AccountReportQuery,
  AccountReportResponse,
  AccountReportSummaryRow,
  ReportType,
} from "../../services/analytics.types";

interface PaginationState {
  page: number;
  pageSize: number;
}

interface AccountReportResultsProps {
  reportType: ReportType;
  data: AccountReportResponse;
  loading?: boolean;
  paginationState: PaginationState;
  handlePaginationChange?: (model: PaginationState) => void;
  reportFilters: AccountReportQuery;
  showExportButton?: boolean;
}

export const AccountReportResults = ({
  reportType,
  data,
  loading = false,
  paginationState,
  handlePaginationChange = () => {},
  reportFilters,
  showExportButton = false,
}: AccountReportResultsProps) => {
  const { isPdfGenerating, pdfSuccess, prevFilters, handleDownloadPdfClick } = useReportPdfDownload({
    filters: reportFilters,
    download: AnalyticsService.downloadAccountReportPdf,
    fileNamePrefix: `account-report-${reportType}`,
  });

  const withRowMeta = withReportRowMeta(paginationState.page * paginationState.pageSize);

  let tableRows: object[];
  if (reportType === "summary") {
    const rows = (data.rows as AccountReportSummaryRow[]).map(withRowMeta);
    const subtotal = {
      id: "subtotal",
      label: "Subtotal",
      totalOpeningBalance: sumReportField(rows, "openingBalance"),
      totalCreditSum: sumReportField(rows, "totalCredit"),
      totalDebitSum: sumReportField(rows, "totalDebit"),
      totalClosingBalance: sumReportField(rows, "closingBalance"),
    };
    tableRows = rows.length ? [...rows, subtotal] : rows;
  } else {
    // one row per (transaction, account): a transfer between two company accounts shows on both sides
    const rows = (data.rows as AccountReportDetailedRow[]).map(toAccountDetailedTableRow).map(withRowMeta);
    const subtotal = {
      id: "subtotal",
      label: "Subtotal",
      totalCredit: sumReportField(rows, "creditAmount"),
      totalDebit: sumReportField(rows, "debitAmount"),
    };
    tableRows = rows.length ? [...rows, subtotal] : rows;
  }

  return (
    <CardLayout>
      <MTable
        // Export data
        showExportButton={showExportButton}
        isPdfGenerating={isPdfGenerating}
        pdfSuccess={pdfSuccess}
        currentFilters={reportFilters}
        prevFilters={prevFilters}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={accountReportColumns[reportType]}
        tableRows={tableRows}
        rowCount={data.count}
        loading={loading}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
      />
    </CardLayout>
  );
};
